import { useRef, useState } from 'react'

import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus'

import Icon from '@space-core/components/icon'
import Loader from '@space-core/components/loader'
import { Actions, Entities } from '@space-core/datahub'
import { useManageModal } from '@space-core/hooks'

import Collapse from '@space-shell/common/components/newCollapse'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import useRequest from '@space-shell/common/hooks/useRequest'
import type { ExternalLink } from '@space-shell/common/services/externalLink'
import {
  ExternalLinkEndpoints,
  ExternalLinkService
} from '@space-shell/common/services/externalLink'
import { UserInfoRole } from '@space-shell/common/services/user'
import { RoleType, useMembershipStore, useViewAsStore } from '@space-shell/core/store'
import { useUserInfoStore } from '@space-shell/core/store/useUserInfoStore'
import { useTranslation } from 'react-i18next'

import styled from 'styled-components'

import { ExternalLinkList } from './externalLinkList'
import { ExternalLinkModal } from './externalLinkModal'
import { ExternalLinkModalRemove } from './externalLinkModalRemove'

const Title = styled.h2`
  font-size: 0.875rem;
  line-height: 1.5;
  width: auto;

  & .text-box {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
    font-size: inherit;
    line-height: inherit;
  }
`

const ActionBtn = styled.button`
  padding: 0.375rem !important;
`

const ButtonIcon = styled(Icon)`
  width: 0.8125rem;
  height: 0.8125rem;
`

const ExternalLinksMenu = () => {
  const { t } = useTranslation()

  const [linkId, setLinkId] = useState<string>('')
  const collapseRef = useRef<HTMLHcCollapseItemElement>(null)
  const [selectedLink, setSelectedLink] = useState<ExternalLink>()

  const user = useUserInfoStore()
  const removeModal = useManageModal()
  const { authStatus } = useAuthContext()
  const externalLinkModal = useManageModal()
  const { role: viewAsRole } = useViewAsStore()
  const { membershipSlug } = useMembershipStore()
  const { sendDataHubEvent } = useDataHubEvents()

  const { isLoading, data: externalLinks } = useRequest(
    [ExternalLinkEndpoints.list(membershipSlug)],
    () => ExternalLinkService.list(membershipSlug)
  )

  function addLink() {
    sendDataHubEvent({ group: Entities.EXTERNAL_LINKS, name: Actions.CLICK_ADD })
    setSelectedLink(undefined)
    externalLinkModal.handleOpen()
  }

  function handleEditLink(selectedLink: ExternalLink) {
    sendDataHubEvent({ group: Entities.EXTERNAL_LINKS, name: Actions.CLICK_EDIT })
    setSelectedLink(selectedLink)
    externalLinkModal.handleOpen()
  }

  function handleRemoveLink(linkId: string) {
    sendDataHubEvent({ group: Entities.EXTERNAL_LINKS, name: Actions.CLICK_REMOVE })
    setLinkId(linkId)
    removeModal.handleOpen()
  }

  const isOwner = user?.role === UserInfoRole.OWNER

  if (isLoading || authStatus === AuthStatus.AUTHENTICATING) {
    return <Loader full />
  }

  if (!externalLinks?.length && !isOwner) {
    return null
  }

  return (
    <>
      <Collapse>
        <Collapse.CollapseItem ref={collapseRef} className='_border-top' active>
          <div slot='title' className='_text-gray-900 _w-full'>
            <div className='_d-flex'>
              <Title className='_mb-0 _align-self-center _flex-grow-1 _text-gray-900 _font-weight-bold'>
                <span className='text-box'>{t('external_links.title')}</span>
              </Title>

              {isOwner && viewAsRole === RoleType.Creator && (
                <ActionBtn
                  onClick={addLink}
                  data-test='add-link'
                  className='hot-button _d-flex _align-items-center _border-0'>
                  <ButtonIcon icon={faPlus} className='_text-gray-900' />
                </ActionBtn>
              )}
            </div>
          </div>

          <div slot='content'>
            <ExternalLinkList
              handleAddLink={addLink}
              linkList={externalLinks ?? []}
              handleEditLink={handleEditLink}
              handleRemoveLink={handleRemoveLink}
              isOwner={isOwner && viewAsRole === RoleType.Creator}
            />
          </div>
        </Collapse.CollapseItem>
      </Collapse>

      <ExternalLinkModal
        selectedLink={selectedLink}
        isOpen={externalLinkModal.isModalOpen}
        handleClose={externalLinkModal.handleClose}
      />
      <ExternalLinkModalRemove
        linkId={linkId}
        isOpen={removeModal.isModalOpen}
        handleClose={removeModal.handleClose}
      />
    </>
  )
}

export default ExternalLinksMenu
